import mammoth from "mammoth";

export const HYMN_CSS = `
      p {
        white-space: pre;
        font-family: "Trebuchet MS", "Lucida Sans Unicode", "Lucida Grande", "Tahoma", monospace;
        font-size: 1.5em;
        padding-bottom: 0.5em;
        margin: 0;
        color: black;
      }
      p:has(.chords) {
        padding-bottom: 0;
      }
      .chords {
        color: red;
        font-family: "Georgia", "Palatino Linotype", "Book Antiqua", monospace;
        font-weight: bold;
        padding-bottom: 0;
      }
    `;

export function buildHymnHtml(bodyInnerHtml) {
  return `<!DOCTYPE html>
<html lang="el">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>${HYMN_CSS}</style>
</head>
<body>
${bodyInnerHtml}
</body>
</html>`;
}

export function parseHymnHtml(fullHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(fullHtml, "text/html");
  const body = doc.body;
  if (!body) return "";
  return body.innerHTML.trim();
}

export async function convertDocxToHtml(file) {
  const arrayBuffer = await file.arrayBuffer();
  const options = {
    includeDefaultStyleMap: true,
    convertImage: mammoth.images.imgElement(function (image) {
      return image.read("base64").then(function (imageBuffer) {
        return {
          src: "data:" + image.contentType + ";base64," + imageBuffer,
        };
      });
    }),
  };
  const result = await mammoth.convertToHtml({ arrayBuffer }, options);
  if (result.messages.length > 0) {
    console.warn("Mammoth conversion messages:", result.messages);
  }
  let processedHtml = result.value;
  processedHtml = processedHtml.replace(/style=([^"'>\s]+)/gi, 'style="$1"');
  processedHtml = processedHtml.replace(
    /color\s*:\s*#([Ee]{2}[0]{4})/g,
    "color:#EE0000"
  );
  const htmlContent = `<!DOCTYPE html>
<html lang="el">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body>
  ${processedHtml}
</body>
</html>`;
  const blob = new Blob([htmlContent], { type: "text/html" });
  return new File([blob], file.name.replace(/\.docx$/i, ".html"), {
    type: "text/html",
  });
}

const isRedColor = (styleAttr) => {
  if (!styleAttr) return false;
  const styleLower = styleAttr.toLowerCase();
  const redColorPattern =
    /color\s*:\s*(red|#ee0000|#ee00|#ff0000|#ff00|rgb\s*\(\s*238\s*,\s*0\s*,\s*0\s*\)|rgb\s*\(\s*255\s*,\s*0\s*,\s*0\s*\)|rgba\s*\(\s*238\s*,\s*0\s*,\s*0\s*[^)]*\)|rgba\s*\(\s*255\s*,\s*0\s*,\s*0\s*[^)]*\))/i;
  return redColorPattern.test(styleLower);
};

export function isChordPattern(text) {
  const cleanText = text.replace(/<[^>]*>/g, "").trim();
  if (!cleanText || cleanText.length === 0) return false;
  if (cleanText.length > 150) return false;
  const chordRegex =
    /^[A-Ga-g][#b]?[majsusdimadd]?[0-9]*(\s+[A-Ga-g][#b]?[majsusdimadd]?[0-9]*)*[\s\-\\.]*$/;
  const greekCharCount = (cleanText.match(/[Ͱ-Ͽ]/g) || []).length;
  const totalCharCount = cleanText.replace(/\s/g, "").length;
  const greekRatio = totalCharCount > 0 ? greekCharCount / totalCharCount : 0;
  if (greekRatio > 0.3) return false;
  return chordRegex.test(cleanText);
}

export async function cleanHtmlFile(file) {
  const htmlText = await file.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlText, "text/html");
  const body = doc.body;
  if (!body) {
    throw new Error("No <body> tag found.");
  }
  const cleanedContent = [];

  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent.replace(/ /g, "&nbsp;").replace(/\n/g, " ");
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName?.toLowerCase();
      const styleAttr = node.getAttribute("style") || "";
      const styleLower = styleAttr.toLowerCase();
      const isRed = isRedColor(styleAttr);
      const isBold =
        tagName === "b" ||
        tagName === "strong" ||
        styleLower.includes("font-weight:bold") ||
        styleLower.includes("font-weight:700");
      let content = "";
      Array.from(node.childNodes).forEach((child) => {
        content += processNode(child);
      });
      if (tagName === "span" && isRed) {
        const hasBoldInStyle =
          styleLower.includes("font-weight:bold") ||
          styleLower.includes("font-weight:700");
        if (hasBoldInStyle) {
          return `<span class="chords"><b>${content}</b></span>`;
        }
        return `<span class="chords">${content}</span>`;
      } else if (tagName === "b" || tagName === "strong" || isBold) {
        const parentElement = node.parentElement;
        const parentStyle = parentElement?.getAttribute("style") || "";
        if (isRedColor(parentStyle)) {
          return `<span class="chords"><strong>${content}</strong></span>`;
        }
        return `<strong>${content}</strong>`;
      } else if (tagName === "span") {
        let hasRedChild = false;
        const redSpans = node.querySelectorAll(
          "span[style*='color'], span[style*='Color'], span[style*='COLOR']"
        );
        for (const redSpan of redSpans) {
          if (isRedColor(redSpan.getAttribute("style") || "")) {
            hasRedChild = true;
            break;
          }
        }
        if (!hasRedChild && styleAttr) {
          hasRedChild = isRedColor(styleAttr);
        }
        if (hasRedChild || isRed) {
          const hasBold =
            styleLower.includes("font-weight:bold") ||
            styleLower.includes("font-weight:700") ||
            node.querySelector("b, strong");
          if (hasBold) {
            return `<span class="chords"><b>${content}</b></span>`;
          }
          return `<span class="chords">${content}</span>`;
        }
        if (styleAttr && styleAttr.trim()) {
          return `<span style="${styleAttr}">${content}</span>`;
        }
        return content;
      } else if (tagName === "p" || tagName === "div") {
        return content;
      } else {
        return content;
      }
    }
    return "";
  };

  const paragraphs = body.querySelectorAll("p");
  paragraphs.forEach((p) => {
    const paragraphText = p.textContent || "";
    const isChordLine = isChordPattern(paragraphText);
    let line = "";
    Array.from(p.childNodes).forEach((child) => {
      line += processNode(child);
    });
    if (line.trim() !== "") {
      if (isChordLine) {
        const cleanLine = line.replace(/<[^>]*>/g, "").trim();
        const normalizedLine = cleanLine.replace(/\s+/g, " ");
        cleanedContent.push(
          `<p><span class="chords"><strong>${normalizedLine}</strong></span></p>`
        );
      } else {
        cleanedContent.push(`<p>${line}</p>`);
      }
    }
  });

  const finalHtml = buildHymnHtml(cleanedContent.join("\n  "));
  const blob = new Blob([finalHtml], { type: "text/html" });
  return new File([blob], file.name.replace(/\.htm[l]?$/, "_cleaned.html"), {
    type: "text/html",
  });
}
